import { ArrowLeft, Eye, EyeOff, IdCard, Lock, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { CommanServices } from '../services/commanServices.jsx';

const copy = {
  en: {
    back: 'Back to login',
    title: 'Reset your password',
    description: 'Verify your account details and choose a new password.',
    emailLabel: 'Email address',
    emailPlaceholder: 'Enter your email address',
    employeeIdLabel: 'Employee ID',
    employeeIdPlaceholder: 'Enter your employee ID',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'Enter your new password',
    confirmPasswordLabel: 'Confirm new password',
    confirmPasswordPlaceholder: 'Re-enter your new password',
    showNewPassword: 'Show new password',
    hideNewPassword: 'Hide new password',
    showConfirmPassword: 'Show confirmed password',
    hideConfirmPassword: 'Hide confirmed password',
    emailRequired: 'Please enter your email address',
    emailInvalid: 'Please enter a valid email address',
    employeeIdRequired: 'Please enter your employee ID',
    passwordsRequired: 'Please fill in all password fields',
    passwordsMismatch: 'Passwords do not match',
    success: 'Password updated successfully! Please log in with your new password.',
    updateFailed: 'Failed to update password',
    accountNotFound: 'No account found with that email address',
    unexpectedError: 'An error occurred during password update',
    resetting: 'Resetting…',
    resetPassword: 'Reset password',
  },
  ar: {
    back: 'العودة إلى تسجيل الدخول',
    title: 'إعادة تعيين كلمة المرور',
    description: 'تحقق من بيانات حسابك واختر كلمة مرور جديدة.',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    employeeIdLabel: 'الرقم الوظيفي',
    employeeIdPlaceholder: 'أدخل رقمك الوظيفي',
    newPasswordLabel: 'كلمة المرور الجديدة',
    newPasswordPlaceholder: 'أدخل كلمة المرور الجديدة',
    confirmPasswordLabel: 'تأكيد كلمة المرور الجديدة',
    confirmPasswordPlaceholder: 'أعد إدخال كلمة المرور الجديدة',
    showNewPassword: 'إظهار كلمة المرور الجديدة',
    hideNewPassword: 'إخفاء كلمة المرور الجديدة',
    showConfirmPassword: 'إظهار كلمة المرور المؤكدة',
    hideConfirmPassword: 'إخفاء كلمة المرور المؤكدة',
    emailRequired: 'يرجى إدخال بريدك الإلكتروني',
    emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح',
    employeeIdRequired: 'يرجى إدخال رقمك الوظيفي',
    passwordsRequired: 'يرجى تعبئة جميع حقول كلمة المرور',
    passwordsMismatch: 'كلمتا المرور غير متطابقتين',
    success: 'تم تحديث كلمة المرور بنجاح! يرجى تسجيل الدخول باستخدام كلمة المرور الجديدة.',
    updateFailed: 'تعذر تحديث كلمة المرور',
    accountNotFound: 'لم يتم العثور على حساب مرتبط بهذا البريد الإلكتروني',
    unexpectedError: 'حدث خطأ أثناء تحديث كلمة المرور',
    resetting: 'جارٍ إعادة التعيين…',
    resetPassword: 'إعادة تعيين كلمة المرور',
  },
};

const ForgotPasswordModal = ({
  error,
  setError,
  loading,
  setLoading,
  setForgotPasswordMode,
  language = 'en',
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isArabic = language === 'ar';
  const text = isArabic ? copy.ar : copy.en;

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    const emailInput = event.currentTarget.elements.email;

    if (!email.trim()) {
      setError(text.emailRequired);
      return;
    }

    if (emailInput?.validity?.typeMismatch) {
      setError(text.emailInvalid);
      return;
    }

    if (!employeeId.trim()) {
      setError(text.employeeIdRequired);
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError(text.passwordsRequired);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(text.passwordsMismatch);
      return;
    }

    setLoading(true);
    try {
      const res = await CommanServices.forgotPassword({
        email: email.trim(),
        employee_id: employeeId.trim(),
        new_password: newPassword,
      });

      if (res && res.status) {
        setSuccessMessage(text.success);
        alert(text.success);
        setNewPassword('');
        setConfirmPassword('');
        setForgotPasswordMode(false);
        setSuccessMessage('');
      } else {
        setError(res?.message || text.updateFailed);
      }
    } catch (err) {
      const resp = err?.response;
      if (resp && resp.data) {
        const detail = resp.data.detail || resp.data.message;
        if (resp.status === 404) {
          setError(detail || text.accountNotFound);
        } else {
          setError(detail || text.unexpectedError);
        }
      } else {
        setError(err?.message || text.unexpectedError);
      }
    } finally {
      setLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setForgotPasswordMode(false);
    setError('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <section dir={isArabic ? 'rtl' : 'ltr'} aria-labelledby="forgot-password-title">
      <button className="dms-auth-back" type="button" onClick={closeForgotPassword}>
        <ArrowLeft aria-hidden="true" />
        <span>{text.back}</span>
      </button>

      <h1 id="forgot-password-title" className="dms-auth-card-title">
        {text.title}
      </h1>
      <p className="dms-auth-card-copy">{text.description}</p>

      <form className="dms-auth-form dms-forgot-form" onSubmit={handleForgotPassword} noValidate>
        <div className="dms-auth-group">
          <label className="dms-auth-label" htmlFor="forgot-password-email">
            {text.emailLabel}
          </label>
          <div className="dms-auth-field">
            <Mail className="dms-auth-field-icon" aria-hidden="true" />
            <input
              id="forgot-password-email"
              name="email"
              type="email"
              dir="ltr"
              autoComplete="email"
              placeholder={text.emailPlaceholder}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="dms-auth-input"
              required
            />
          </div>
        </div>

        <div className="dms-auth-group">
          <label className="dms-auth-label" htmlFor="forgot-password-employee-id">
            {text.employeeIdLabel}
          </label>
          <div className="dms-auth-field">
            <IdCard className="dms-auth-field-icon" aria-hidden="true" />
            <input
              id="forgot-password-employee-id"
              name="employeeId"
              type="text"
              autoComplete="off"
              placeholder={text.employeeIdPlaceholder}
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
              className="dms-auth-input"
              required
            />
          </div>
        </div>

        <div className="dms-auth-group">
          <label className="dms-auth-label" htmlFor="forgot-password-new-password">
            {text.newPasswordLabel}
          </label>
          <div className="dms-auth-field">
            <Lock className="dms-auth-field-icon" aria-hidden="true" />
            <input
              id="forgot-password-new-password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={text.newPasswordPlaceholder}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="dms-auth-input"
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword((isVisible) => !isVisible)}
              className="dms-auth-icon-button"
              aria-label={showNewPassword ? text.hideNewPassword : text.showNewPassword}
              aria-pressed={showNewPassword}
            >
              {showNewPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
        </div>

        <div className="dms-auth-group">
          <label className="dms-auth-label" htmlFor="forgot-password-confirm-password">
            {text.confirmPasswordLabel}
          </label>
          <div className="dms-auth-field">
            <Lock className="dms-auth-field-icon" aria-hidden="true" />
            <input
              id="forgot-password-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={text.confirmPasswordPlaceholder}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="dms-auth-input"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((isVisible) => !isVisible)}
              className="dms-auth-icon-button"
              aria-label={showConfirmPassword ? text.hideConfirmPassword : text.showConfirmPassword}
              aria-pressed={showConfirmPassword}
            >
              {showConfirmPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
        </div>

        {error && (
          <p className="dms-auth-alert dms-auth-alert--error" role="alert">
            {error}
          </p>
        )}
        {successMessage && (
          <p className="dms-auth-alert dms-auth-alert--success" role="status">
            {successMessage}
          </p>
        )}

        <button className="dms-auth-submit" disabled={loading} type="submit">
          {loading ? text.resetting : text.resetPassword}
        </button>
      </form>
    </section>
  );
};

export default ForgotPasswordModal;
